import { useEffect, useRef, useState } from "react";

const SPEED_BASE = 0.6; // same as host

export default function SniperPlayer({
  turnData, shotResult, playerName,
  onFire,
}) {
  const posRef   = useRef(0);
  const dirRef   = useRef(1);
  const rafRef   = useRef(null);
  const firedRef = useRef(false);  // prevent double-fire
  const [phase, setPhase] = useState("waiting"); // waiting | aiming | result | eliminated

  const zoneW = turnData?.zone_w ?? shotResult?.zone_w ?? 40;
  const myTurn = turnData?.current_player === playerName;
  const isElim = (shotResult?.eliminated ?? turnData?.eliminated ?? []).includes(playerName);
  const myScore = (turnData?.scores ?? shotResult?.scores ?? {})[playerName] ?? 0;

  // Phase transitions driven by props
  useEffect(() => {
    if (isElim) {
      setPhase("eliminated");
      return;
    }
    if (shotResult) {
      setPhase("result");
      firedRef.current = false;
      return;
    }
    if (turnData) {
      setPhase(myTurn ? "aiming" : "watching");
      if (myTurn) firedRef.current = false;
    }
  }, [turnData, shotResult, myTurn, isElim]);

  // Stop RAF when not aiming
  useEffect(() => {
    if (phase !== "aiming") {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    }
  }, [phase]);

  // Fire handler: read posRef at moment of tap
  function handleFire() {
    if (firedRef.current) return;
    firedRef.current = true;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    onFire(posRef.current);
  }

  // Shared zone label
  const half  = zoneW / 2;
  const lo    = (50 - half).toFixed(0);
  const hi    = (50 + half).toFixed(0);

  // ── AIMING ──────────────────────────────────────────────────────────────────
  if (phase === "aiming") {
    return <AimingScreen zoneW={zoneW} posRef={posRef} dirRef={dirRef} rafRef={rafRef} onFire={handleFire} myScore={myScore} />;
  }

  // ── WATCHING ──────────────────────────────────────────────────────────────
  if (phase === "watching") {
    const shooter = turnData?.current_player ?? "";
    return (
      <div style={{ textAlign: "center", padding: "2rem 1rem" }}>
        <div style={{ fontSize: "3rem", marginBottom: "0.75rem" }}>👀</div>
        <p style={{ fontFamily: "'Fredoka One', sans-serif", fontSize:"var(--fs-xl)", color: "#9b5de5" }}>
          Стреляет: <strong style={{ color: "#ffe600" }}>{shooter}</strong>
        </p>
        <p style={{ fontFamily: "'Nunito', sans-serif", color: "#555", fontSize:"var(--fs-sm)", marginTop: "0.5rem" }}>
          Зона: {lo}% – {hi}% · Жди своего хода
        </p>
        <p style={{ fontFamily: "'Nunito', sans-serif", color: "#333", fontSize:"var(--fs-sm)", marginTop: "0.75rem" }}>
          Твои очки: <span style={{ color: "#ffe600" }}>{myScore}</span>
        </p>
      </div>
    );
  }

  // ── RESULT ──────────────────────────────────────────────────────────────────
  if (phase === "result") {
    const myShot = shotResult?.player === playerName;
    const hit    = shotResult?.hit;
    const score  = shotResult?.score ?? 0;
    const shooter = shotResult?.player ?? "";
    return (
      <div style={{ textAlign: "center", padding: "2rem 1rem" }}>
        {myShot ? (
          <>
            <div style={{ fontSize: "3.5rem", marginBottom: "0.5rem" }}>
              {hit ? "🎯" : "💀"}
            </div>
            <p style={{
              fontFamily: "'Fredoka One', sans-serif", fontSize:"var(--fs-xl)",
              color: hit ? "#3dffa0" : "#f44336", marginBottom: "0.5rem",
            }}>
              {hit ? `Попал! +${score} очков` : "Промах — ты вылетаешь!"}
            </p>
            {hit && (
              <p style={{ fontFamily: "'Nunito', sans-serif", color: "#aaa", fontSize:"var(--fs-md)" }}>
                Точность: <strong style={{ color: "#ffe600" }}>{score}%</strong> от идеала
              </p>
            )}
          </>
        ) : (
          <>
            <div style={{ fontSize: "3rem", marginBottom: "0.5rem" }}>
              {hit ? "✅" : "❌"}
            </div>
            <p style={{ fontFamily: "'Fredoka One', sans-serif", fontSize:"var(--fs-lg)", color: hit ? "#3dffa0" : "#f44336" }}>
              {shooter}: {hit ? `попал (+${score})` : "промахнулся"}
            </p>
          </>
        )}
        <p style={{ fontFamily: "'Nunito', sans-serif", color: "#333", fontSize:"var(--fs-sm)", marginTop: "1rem" }}>
          Твои очки: <span style={{ color: "#ffe600" }}>{myScore}</span>
        </p>
      </div>
    );
  }

  // ── ELIMINATED ───────────────────────────────────────────────────────────────
  if (phase === "eliminated") {
    return (
      <div style={{ textAlign: "center", padding: "2rem 1rem" }}>
        <div style={{ fontSize: "3.5rem", marginBottom: "0.75rem" }}>💀</div>
        <p style={{ fontFamily: "'Fredoka One', sans-serif", fontSize:"var(--fs-xl)", color: "#f44336" }}>
          Ты вылетел!
        </p>
        <p style={{ fontFamily: "'Nunito', sans-serif", color: "#555", fontSize:"var(--fs-sm)", marginTop: "0.5rem" }}>
          Смотри как выживают остальные
        </p>
        <p style={{ fontFamily: "'Nunito', sans-serif", color: "#333", fontSize:"var(--fs-sm)", marginTop: "1rem" }}>
          Итого очков: <span style={{ color: "#ffe600" }}>{myScore}</span>
        </p>
      </div>
    );
  }

  // ── WAITING (до начала) ──────────────────────────────────────────────────────
  return (
    <div style={{ textAlign: "center", padding: "2rem 1rem" }}>
      <div style={{ fontSize: "3rem", marginBottom: "0.75rem" }}>🎯</div>
      <p style={{ fontFamily: "'Fredoka One', sans-serif", fontSize:"var(--fs-xl)", color: "#9b5de5" }}>
        Снайпер
      </p>
      <p style={{ fontFamily: "'Nunito', sans-serif", color: "#555", fontSize:"var(--fs-sm)", marginTop: "0.5rem" }}>
        Жди своей очереди
      </p>
    </div>
  );
}

// ── Aiming UI — RAF внутри, кнопка огня ─────────────────────────────────────
function AimingScreen({ zoneW, posRef, dirRef, rafRef, onFire, myScore }) {
  const [displayPos, setDisplayPos] = useState(posRef.current);
  const firedRef2 = useRef(false);

  useEffect(() => {
    firedRef2.current = false;
    const speed = SPEED_BASE;

    function loop() {
      posRef.current += dirRef.current * speed;
      if (posRef.current >= 100) { posRef.current = 100; dirRef.current = -1; }
      if (posRef.current <= 0)   { posRef.current = 0;   dirRef.current =  1; }
      setDisplayPos(posRef.current);
      rafRef.current = requestAnimationFrame(loop);
    }
    rafRef.current = requestAnimationFrame(loop);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, []); // eslint-disable-line

  const half = zoneW / 2;
  const inZone = displayPos >= (50 - half) && displayPos <= (50 + half);

  // Visual bar: 0–100% width of container
  const BAR_W = 280; // px reference for display
  const arrowLeft = `${displayPos}%`;
  const zoneLeft  = `${50 - half}%`;
  const zoneWidth = `${zoneW}%`;

  function fire() {
    if (firedRef2.current) return;
    firedRef2.current = true;
    onFire();
  }

  return (
    <div style={{ padding: "1rem", display: "flex", flexDirection: "column", alignItems: "center", gap: "1.2rem" }}>
      <p style={{ fontFamily: "'Fredoka One', sans-serif", fontSize:"var(--fs-xl)", color: "#ffe600", textAlign: "center" }}>
        🎯 Твой выстрел!
      </p>

      {/* Visual strip */}
      <div style={{
        width: "100%", position: "relative",
        height: "56px",
        background: "#0d0025",
        border: "2px solid #9b5de533",
        borderRadius: "0.75rem",
        overflow: "hidden",
      }}>
        {/* Zone */}
        <div style={{
          position: "absolute", top: 0, bottom: 0,
          left: zoneLeft, width: zoneWidth,
          background: "#3dffa022",
          borderLeft: "2px solid #3dffa0",
          borderRight: "2px solid #3dffa0",
          transition: "none",
        }} />
        {/* Centre dashed */}
        <div style={{
          position: "absolute", top: "10%", bottom: "10%",
          left: "50%", width: "2px",
          background: "repeating-linear-gradient(to bottom, #3dffa066 0px, #3dffa066 4px, transparent 4px, transparent 8px)",
        }} />
        {/* Arrow */}
        <div style={{
          position: "absolute",
          left: arrowLeft,
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: 0, height: 0,
          borderLeft: "10px solid transparent",
          borderRight: "10px solid transparent",
          borderTop: `22px solid ${inZone ? "#ffe600" : "#ffe600"}`,
          filter: `drop-shadow(0 0 8px #ffe600)`,
          transition: "none",
        }} />
      </div>

      {/* Zone info */}
      <p style={{ fontFamily: "'Nunito', sans-serif", fontSize:"var(--fs-sm)", color: "#555", textAlign: "center" }}>
        Зелёная зона: {(50 - half).toFixed(0)}% – {(50 + half).toFixed(0)}%
        {" · "}Центр = 100 очков
      </p>

      {/* FIRE button — big, hard to miss */}
      <button
        onPointerDown={fire}
        style={{
          width: "100%", maxWidth: "320px",
          padding: "1.2rem",
          background: "#ff3355",
          border: "3px solid #000",
          borderRadius: "1rem",
          boxShadow: "4px 4px 0 #000",
          fontFamily: "'Fredoka One', sans-serif",
          fontSize:"var(--fs-2xl)",
          color: "#fff",
          cursor: "pointer",
          userSelect: "none",
          WebkitUserSelect: "none",
          touchAction: "manipulation",
          letterSpacing: "0.05em",
        }}
        onMouseEnter={e => e.currentTarget.style.transform = "translate(-2px,-2px)"}
        onMouseLeave={e => e.currentTarget.style.transform = "none"}
      >
        🔫 ОГОНЬ!
      </button>

      <p style={{ fontFamily: "'Nunito', sans-serif", color: "#333", fontSize:"var(--fs-sm)" }}>
        Твои очки: <span style={{ color: "#ffe600" }}>{myScore}</span>
      </p>
    </div>
  );
}
